// Derives race events from consecutive stamped frames. Each event carries the signal time it happened;
// the timeline delivers it when render time reaches it.
import { officialResults, standings } from './standings';
import { CAT_IDS, type CatId, type RaceEvent, type StampedFrame } from './types';

/** A pause between running frames longer than this is a connection gap, not jitter. */
export const GAP_MS = 300;
/** A backwards step larger than this that is not a catnip landing is treated as a discontinuity. */
export const BACK_STEP = 0.008;
export const LEAD_HOLD_MS = 500;
/** Cats crossing less than this apart make a photo finish (the finish camera uses the same limit on official times). */
export const CLOSE_FINISH_MS = 500;
/** No lead changes until someone has really left the gate, so the start does not flicker. */
const LEAD_MIN_POS = 0.02;

export type Discontinuity = 'race-start' | 'landing' | 'jump';

/** What the deriver learned from one frame: events, plus per-cat segment breaks for smoothing. */
export interface FrameFacts {
  events: RaceEvent[];
  breaks: (Discontinuity | null)[]; // per lane; a break means "do not smooth across the step into this frame"
  finishPlace: (number | undefined)[]; // per lane, as of this frame
}

export class EventDeriver {
  private prev: StampedFrame | null = null;
  private raceNo = -1;
  private finishOrder: number[] = []; // lanes in crossing order
  private finishT: number[] = [];
  private leader: number | null = null;
  private candidate: { lane: number; since: number } | null = null;

  push(f: StampedFrame): FrameFacts {
    const prev = this.prev;
    this.prev = f;
    const events: RaceEvent[] = [];
    const breaks: (Discontinuity | null)[] = CAT_IDS.map(() => null);

    if (f.raceNo !== this.raceNo) {
      this.raceNo = f.raceNo;
      this.finishOrder = [];
      this.finishT = [];
      this.leader = null;
      this.candidate = null;
    }
    if (!prev || prev.state !== f.state || prev.raceNo !== f.raceNo) {
      events.push({ type: 'phase', t: f.t, phase: f.state, raceNo: f.raceNo });
    }

    if (f.state === 'running' && f.cats) {
      const same = prev?.state === 'running' && prev.raceNo === f.raceNo && prev.cats;
      if (!same) {
        breaks.fill('race-start');
        if (f.ham) events.push({ type: 'ham-moved', t: f.t, lane: f.ham.lane, pos: f.ham.pos });
      } else {
        this.compare(prev, f, events, breaks);
      }
      this.finishes(same ? prev : null, f, events);
      this.lead(f, events);
    }

    if (f.state === 'finished' && f.times && !(prev?.state === 'finished' && prev.raceNo === f.raceNo)) {
      // Cats whose crossing frame we never saw (the last cat usually finishes in the final frame) cross now.
      const results = officialResults(f.times);
      if (prev?.raceNo === f.raceNo && prev.state === 'running') {
        const missing = results.filter((r) => !this.finishOrder.includes(CAT_IDS.indexOf(r.id)))
          .sort((a, b) => a.seconds - b.seconds);
        for (const r of missing) this.cross(CAT_IDS.indexOf(r.id), f.t, events);
      }
      events.push({ type: 'results', t: f.t, results });
    }

    const finishPlace = CAT_IDS.map((_, lane) => {
      const i = this.finishOrder.indexOf(lane);
      return i >= 0 ? i + 1 : undefined;
    });
    return { events, breaks, finishPlace };
  }

  private compare(prev: StampedFrame, f: StampedFrame, events: RaceEvent[], breaks: (Discontinuity | null)[]): void {
    const gap = f.t - prev.t > GAP_MS;
    for (let lane = 0; lane < 6; lane++) {
      const a = prev.cats![lane]!, b = f.cats![lane]!;
      const cat = CAT_IDS[lane]!;
      if (!a.ham && b.ham) events.push({ type: 'ham-start', t: f.t, cat });
      if (a.ham && !b.ham) events.push({ type: 'ham-end', t: f.t, cat });
      if (!a.nip && b.nip) events.push({ type: 'catnip-catch', t: f.t, cat, at: b.pos }); // where it stays frozen
      const landed = a.nip && !b.nip;
      const backAcrossGap = gap && !a.nip && !b.nip && a.pos - b.pos > BACK_STEP;
      if (landed || backAcrossGap) {
        events.push({ type: 'catnip-landing', t: f.t, cat, from: a.pos, to: b.pos, delta: b.pos - a.pos, duringGap: gap });
        breaks[lane] = 'landing';
      } else if (a.pos - b.pos > BACK_STEP) {
        breaks[lane] = 'jump';
      }
    }
    const h0 = prev.ham, h1 = f.ham;
    if (h1 && (!h0 || h0.lane !== h1.lane || Math.abs(h0.pos - h1.pos) > 1e-6)) {
      events.push({ type: 'ham-moved', t: f.t, lane: h1.lane, pos: h1.pos });
    }
  }

  private finishes(prev: StampedFrame | null, f: StampedFrame, events: RaceEvent[]): void {
    const crossing = f.cats!
      .map((c, lane) => ({ lane, pos: c.pos, before: prev?.cats?.[lane]?.pos ?? 0 }))
      .filter((c) => c.pos >= 1 && !this.finishOrder.includes(c.lane));
    // Several in one frame: the one that was further ahead in the frame before crossed first.
    crossing.sort((a, b) => b.before - a.before || a.lane - b.lane);
    // Joining a race in which cats have already finished: they keep their places but fire no events.
    const sink = prev ? events : [];
    for (const c of crossing) this.cross(c.lane, f.t, sink);
  }

  private cross(lane: number, t: number, events: RaceEvent[]): void {
    this.finishOrder.push(lane);
    this.finishT.push(t);
    const place = this.finishOrder.length;
    events.push({ type: 'finish', t, cat: CAT_IDS[lane]!, place });
    if (place > 1) {
      const margin = t - this.finishT[place - 2]!;
      if (margin < CLOSE_FINISH_MS) {
        events.push({ type: 'close-finish', t, cats: [CAT_IDS[this.finishOrder[place - 2]!]!, CAT_IDS[lane]!], marginMs: margin });
      }
    }
  }

  private lead(f: StampedFrame, events: RaceEvent[]): void {
    const cats = f.cats!.map((c, lane) => {
      const i = this.finishOrder.indexOf(lane);
      return { lane, signalPos: c.pos, finishPlace: i >= 0 ? i + 1 : undefined };
    });
    const top = standings(cats).findIndex((s) => s.place === 1);
    if (this.leader === null) {
      if (Math.max(...cats.map((c) => c.signalPos)) >= LEAD_MIN_POS) this.leader = top;
      return;
    }
    if (top === this.leader) { this.candidate = null; return; }
    if (!this.candidate || this.candidate.lane !== top) this.candidate = { lane: top, since: f.t };
    if (f.t - this.candidate.since >= LEAD_HOLD_MS) {
      events.push({ type: 'lead-change', t: this.candidate.since, leader: CAT_IDS[top]!, previous: CAT_IDS[this.leader]! as CatId });
      this.leader = top;
      this.candidate = null;
    }
  }
}
